import EventsListingPage from "./pages/EventsListingPage";
import EventDetailPage from "./pages/EventDetailPage";
import MyEventsPage from "./pages/MyEventsPage";
import MyEventDetailPage from "./pages/MyEventDetailPage";
import EventPaymentProcessingPage from "./pages/EventPaymentProcessingPage";
import AdminEventsPage from "./pages/AdminEventsPage";
import AdminEventDetailPage from "./pages/AdminEventDetailPage";
import AdminScannerPage from "./pages/AdminScannerPage";
import AdminCpdPage from "./pages/AdminCpdPage";
import AdminAccreditedInstitutesPage from "./pages/AdminAccreditedInstitutesPage";
import AdminChapterEventRequestsPage from "./pages/AdminChapterEventRequestsPage";
import AdminChapterEventRequestDetailPage from "./pages/AdminChapterEventRequestDetailPage";
import ScanActionPage from "./pages/ScanActionPage";
import PublicRegistrationVerifyPage from "./pages/PublicRegistrationVerifyPage";

const MEMBER_ROLES = [
  "member",
  "executive",
  "chairman",
  "secretary",
  "fellow",
  "firm",
  "past president",
  "probationer",
  "assessor",
  "student",
  "technician",
  "graduate",
];

export const eventsRoutes = [
  // Public / member
  {
    path: "/events",
    element: <EventsListingPage />,
    roles: [...MEMBER_ROLES, "admin"],
  },
  {
    path: "/events/:id",
    element: <EventDetailPage />,
    roles: [...MEMBER_ROLES, "admin"],
  },
  {
    path: "/my-events",
    element: <MyEventsPage />,
    roles: [...MEMBER_ROLES, "admin"],
  },
  {
    path: "/my-events/:regId",
    element: <MyEventDetailPage />,
    roles: [...MEMBER_ROLES, "admin"],
  },
  // Where OnePay returns the member after paying an event fee.
  {
    path: "/my-events/:regId/payment-processing",
    element: <EventPaymentProcessingPage />,
    roles: [...MEMBER_ROLES, "admin"],
  },

  // Admin — gated on the privilege the API already enforces on each of these,
  // not on the admin role, so the sidebar link and the route agree and an
  // executive office granted one of them can actually reach the screen.
  { path: "/admin/events", element: <AdminEventsPage />, perms: ["event.access"] },
  {
    path: "/admin/events/:id",
    element: <AdminEventDetailPage />,
    perms: ["event.access"],
  },
  {
    // The scanner has its own capability — running the door at an event is not
    // the same as being able to read the event.
    path: "/admin/events/:id/scanner",
    element: <AdminScannerPage />,
    perms: ["event.scanner.use"],
  },
  { path: "/admin/cpd", element: <AdminCpdPage />, perms: ["event.cpd.access"] },
  // A chapter's own CPD events, from the chairman's proposal to the allocation
  // that creates the event.
  //
  // On `event.chapter.review`, its OWN capability, not the CPD desk's. Judging
  // what a chapter meeting is worth against the Institute's guideline is the
  // committee's work, and whoever does it should be grantable exactly that
  // without also being handed every member's CPD ledger. Matches the API, which
  // gates these routes the same way and no longer requires an admin role at all.
  // Approving additionally needs `event.chapter.approve`, which the API enforces
  // at the writes and the screen reads off `can_review` / `can_query`.
  {
    path: "/admin/chapter-event-requests",
    element: <AdminChapterEventRequestsPage />,
    perms: ["event.chapter.review"],
  },
  {
    path: "/admin/chapter-event-requests/:id",
    element: <AdminChapterEventRequestDetailPage />,
    perms: ["event.chapter.review"],
  },
  {
    path: "/admin/accredited-institutes",
    element: <AdminAccreditedInstitutesPage />,
    perms: ["event.institute.manage"],
  },

  // QR scan action page — accessible by admin and all member roles.
  //
  // This is where the QR printed on every access card points, so it is scanned
  // by people who are signed in and people who are not. Signed in, it is the
  // desk scanner below. Signed out, `guestRedirect` sends them to the public
  // read-only confirmation of the same card instead of a login box — they can
  // see that the card is genuine and what it admits, and can do nothing to it.
  {
    path: "/scan/:barcode",
    element: <ScanActionPage />,
    roles: [...MEMBER_ROLES, "admin"],
    guestRedirect: (p: Record<string, string | undefined>) => `/verify/${p.barcode ?? ""}`,
  },
];

/**
 * Rendered outside MainLayout and outside every guard — no sidebar, no session,
 * no token. Keep this list to things that are genuinely safe to serve to the
 * open internet.
 */
export const publicEventsRoutes = [
  {
    path: "/verify/:barcode",
    element: <PublicRegistrationVerifyPage />,
  },
];
